/**
 * Extracts `:param` names from a path template string as a union type.
 *
 * @example
 * type P = ExtractParams<'/api/v1/analyses/:analysisId/findings/:findingId'>
 * // => 'analysisId' | 'findingId'
 */
type ExtractParams<T extends string> = T extends `${string}:${infer Param}/${infer Rest}`
  ? Param | ExtractParams<Rest>
  : T extends `${string}:${infer Param}`
    ? Param
    : never

/**
 * Builds a URL path by replacing `:param` placeholders with URL-encoded values.
 *
 * The `params` argument is typesafe — TypeScript infers the required keys from
 * the template literal and provides IDE autocomplete. If the template contains
 * no `:param` placeholders, the `params` argument is omitted entirely.
 *
 * @example
 * buildPath('/api/v1/repositories/:repositoryId', { repositoryId: 'org/repo' })
 * // => '/api/v1/repositories/org%2Frepo'
 *
 * buildPath('/api/v1/patches')
 * // => '/api/v1/patches'
 */
export const buildPath = <T extends string>(
  template: T,
  ...args: [ExtractParams<T>] extends [never] ? [] : [params: Record<ExtractParams<T>, string>]
): string => {
  const params = (args[0] ?? {}) as Record<string, string>
  return template.replace(/:([a-zA-Z][a-zA-Z0-9]*)/g, (_, key: string) => {
    const value = params[key]
    if (value === undefined) {
      throw new Error(`Missing required path parameter: "${key}" in template "${template}"`)
    }
    return encodeURIComponent(value)
  })
}
